import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  CornerDownLeft,
  KeyRound,
  Lock,
  Search,
  Send,
  Settings,
  ShieldAlert,
  Sparkles,
  Square,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/context";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import type { Skill } from "../use-skills";
import type { CatalogModelEntry } from "../use-catalog";
import type { PlaygroundMessage, PlaygroundSession } from "../types";
import { Markdown } from "./markdown";

interface Props {
  session: PlaygroundSession;
  isStreaming: boolean;
  boundMessageId: string | null;
  onSend(content: string): void;
  onStop(): void;
  onUpdateSession(patch: Partial<PlaygroundSession>): void;
  onBindMessage(messageId: string | null): void;
  /** ``null`` while the catalog is still loading; resolves to a bool once fetched. */
  hasActiveCredential: boolean | null;
  /** Dispatchable models, flattened from providers with an active credential. */
  availableModels: string[];
  /** Priced catalog entries (IDR preview + has_pricing per model). */
  catalogModels: CatalogModelEntry[];
  /** While the catalog is in flight we don't render the empty state yet. */
  catalogLoading: boolean;
  /** Workspace skills (Sesi 2a). */
  skills: Skill[];
}

/**
 * Chat column (centred, max-width 780).
 *
 * Top metadata strip → system prompt card → message list → composer.
 * The composer never sends while ``isStreaming`` is true; users abort
 * via the Stop button (red).
 */
export function PlaygroundChatColumn({
  session,
  isStreaming,
  boundMessageId,
  onSend,
  onStop,
  onUpdateSession,
  onBindMessage,
  hasActiveCredential,
  availableModels,
  catalogModels,
  catalogLoading,
  skills,
}: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Always scroll to the bottom on new content / stream tick.
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [session.messages, isStreaming]);

  // Strict BYOK: block send when the tenant has no active credential.
  // ``null`` (catalog still loading) is treated as "unknown" — we keep
  // the composer enabled visually but the backend's pre-flight will
  // catch any genuinely-missing credential.
  const credentialBlocked = hasActiveCredential === false;
  // Send stays disabled until the operator picks a model from the
  // catalog. The playground intentionally has no hardcoded default —
  // pre-filling one would silently land on
  // ``ModelNotAllowedError`` for tenants that block that model, with
  // the user never having an explicit pick to course-correct on.
  const modelMissing = !session.model;
  const submitDisabled = isStreaming || credentialBlocked || modelMissing;

  const submit = useCallback(() => {
    if (!draft.trim() || submitDisabled) return;
    onSend(draft);
    setDraft("");
  }, [draft, submitDisabled, onSend]);

  const onTextareaKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        submit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setDraft("");
      }
    },
    [submit]
  );

  const flagsCount = useMemo(
    () =>
      session.messages.reduce(
        (n, m) =>
          n +
          (m.flagged ? 1 : 0) +
          (m.error ? 1 : 0) +
          (m.redactedTypes?.length ? 1 : 0),
        0
      ),
    [session.messages]
  );

  return (
    <section className="flex h-full min-w-0 flex-col">
      {/* Top metadata strip */}
      <div
        className="flex flex-col gap-2 border-b px-6 py-3"
        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
      >
        {/* Row 1 — title only. The session id pill used to live here,
           but the sessions panel already surfaces it for the active
           session (with the same copy affordance), so the header
           duplicate just wrapped awkwardly under long titles. */}
        <div className="flex min-w-0 items-baseline">
          <EditableTitle
            title={session.title}
            onCommit={(next) => onUpdateSession({ title: next })}
          />
        </div>
        {/* Row 2 — metadata counters + skill/model chips, never wrapped
           into row 1 so chips stay un-squeezed even with a long title. */}
        <div className="mono flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" style={{ color: "var(--pg-text-3)" }}>
          <span>{t("playground.chat.meta.msgs", { n: session.messages.length })}</span>
          <span>{t("playground.chat.meta.tokens", { n: session.stats.tokens })}</span>
          <span
            className={cn(flagsCount > 0 && "rounded-full px-1.5 py-[1px]")}
            style={
              flagsCount > 0
                ? {
                    background: "var(--pg-warn-bg)",
                    color: "var(--pg-warn)",
                    border: "1px solid var(--pg-warn-ring)",
                  }
                : undefined
            }
          >
            {t("playground.chat.meta.flags", { n: flagsCount })}
          </span>
          <SkillChip
            session={session}
            skills={skills}
            disabled={isStreaming}
            onSelect={(skill) => {
              if (skill === null) {
                onUpdateSession({
                  skillRef: null,
                  systemPromptLocked: false,
                });
              } else {
                const version = skill.current_version ?? 1;
                onUpdateSession({
                  skillRef: { skillId: skill.id, version },
                  systemPrompt: skill.current_version_snapshot?.system_prompt ?? "",
                  systemPromptLocked: true,
                });
              }
            }}
          />
          <ModelChip
            model={session.model}
            availableModels={availableModels}
            catalogModels={catalogModels}
            disabled={isStreaming || credentialBlocked}
            onSelect={(next) => onUpdateSession({ model: next })}
          />
        </div>
      </div>

      {/* Scrolling region: system + messages — soft grey so the white
          answer cards lift off the surface. */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-5"
        style={{ background: "var(--pg-border-soft)" }}
      >
        <div className="mx-auto flex w-full max-w-[780px] flex-col gap-4">
          <SystemPromptCard
            session={session}
            skills={skills}
            onUpdate={(patch) => onUpdateSession(patch)}
          />
          {session.messages.length === 0 && !isStreaming && (
            <EmptyPlaceholder />
          )}
          {session.messages.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              isBound={m.id === boundMessageId}
              onBind={() => onBindMessage(m.id)}
            />
          ))}
        </div>
      </div>

      {/* Composer — replaced by a "connect a provider" CTA when the
         strict BYOK pre-flight would 400 the request. */}
      <div className="px-6 pb-5 pt-2">
        {credentialBlocked && !catalogLoading ? (
          <NoCredentialCta />
        ) : (
          <div
            className="mx-auto flex w-full max-w-[780px] flex-col rounded-xl border shadow-sm"
            style={{
              borderColor: "var(--pg-border)",
              background: "var(--pg-surface)",
            }}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onTextareaKeyDown}
              placeholder={t("playground.chat.compose.placeholder")}
              aria-label={t("playground.chat.compose.ariaLabel")}
              rows={3}
              className="block w-full resize-none border-0 bg-transparent px-4 pb-1 pt-3 text-sm leading-relaxed outline-none"
              style={{ color: "var(--pg-text)" }}
              disabled={isStreaming}
            />
            <p
              className="px-4 pb-2 text-xs"
              style={{ color: "var(--pg-text-3)" }}
            >
              {t("playground.chat.compose.disclaimer")}
            </p>
            <div
              className="flex items-center justify-end gap-2 border-t px-3 py-2"
              style={{ borderColor: "var(--pg-border-soft)" }}
            >
              {/* The pii-redact / policies-on / stream-on / temp chips
                 used to live here, but they were hardcoded labels —
                 the FE never read pii_action from the workspace policy
                 and never sent a temperature on the request. Removed
                 rather than left as misleading decoration; the real
                 per-request governance state is on the right-hand
                 panel, and the disclaimer above the textarea covers
                 the always-on guarantees honestly. */}
              {isStreaming ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={onStop}
                >
                  <Square size={12} />
                  {t("playground.chat.compose.stop")}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={submit}
                  disabled={!draft.trim() || submitDisabled}
                >
                  <Send size={12} />
                  {t("playground.chat.compose.send")}
                  <span className="mono ml-1 inline-flex items-center gap-0.5 rounded px-1 py-[1px] text-xs opacity-80">
                    ⌘
                    <CornerDownLeft size={10} />
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function NoCredentialCta() {
  const { t } = useTranslation();
  return (
    <div
      className="mx-auto flex w-full max-w-[780px] flex-col items-start gap-3 rounded-xl border px-5 py-4"
      style={{
        borderColor: "var(--pg-blue-ring)",
        background: "var(--pg-blue-soft)",
        color: "var(--pg-blue-dk)",
      }}
    >
      <div className="flex items-center gap-2">
        <KeyRound size={14} />
        <span className="text-sm font-medium">
          {t("playground.chat.noCredential.title")}
        </span>
      </div>
      <p
        className="text-xs leading-relaxed"
        style={{ color: "var(--pg-text-2)" }}
      >
        {t("playground.chat.noCredential.body")}
      </p>
      <Button variant="default" size="sm" asChild>
        <Link to={ROUTES.PROVIDERS}>
          {t("playground.chat.noCredential.cta")}
        </Link>
      </Button>
    </div>
  );
}

function EditableTitle({
  title,
  onCommit,
}: {
  title: string;
  onCommit(next: string): void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  useEffect(() => {
    setDraft(title);
  }, [title]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="min-w-0 flex-1 truncate text-left text-sm font-medium tracking-tight outline-none"
        style={{ color: "var(--pg-text)" }}
        title={title}
      >
        {title}
      </button>
    );
  }

  return (
    <input
      type="text"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== title) onCommit(trimmed);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          setDraft(title);
          setEditing(false);
        }
      }}
      className="min-w-0 flex-1 truncate border-b bg-transparent text-sm font-medium tracking-tight outline-none"
      style={{
        borderColor: "var(--pg-blue-ring)",
        color: "var(--pg-text)",
      }}
    />
  );
}

function SkillChip({
  session,
  skills,
  disabled,
  onSelect,
}: {
  session: PlaygroundSession;
  skills: Skill[];
  disabled: boolean;
  onSelect(skill: Skill | null): void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!wrapRef.current || !target) return;
      if (!wrapRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const activeSkill = session.skillRef
    ? skills.find((s) => s.id === session.skillRef!.skillId) ?? null
    : null;
  const label = activeSkill
    ? `${activeSkill.name} v${session.skillRef!.version}`
    : t("playground.chat.skill.freeform");

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="mono inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          background: activeSkill ? "var(--pg-blue-soft)" : "var(--pg-bg)",
          border: `1px solid ${activeSkill ? "var(--pg-blue-ring)" : "var(--pg-border)"}`,
          color: activeSkill ? "var(--pg-blue-dk)" : "var(--pg-text-2)",
        }}
        title={
          activeSkill
            ? t("playground.chat.skill.activeTitle")
            : t("playground.chat.skill.selectTitle")
        }
      >
        <Sparkles
          size={11}
          style={{ color: activeSkill ? "var(--pg-blue)" : "var(--pg-text-3)" }}
        />
        {label}
        <ChevronDown size={11} style={{ color: "var(--pg-text-3)" }} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-20 mt-1 max-h-72 w-[260px] overflow-y-auto rounded-lg border py-1 shadow-md"
          style={{
            background: "var(--pg-surface)",
            borderColor: "var(--pg-border)",
          }}
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!activeSkill}
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
              style={{ color: "var(--pg-text-2)" }}
            >
              <span>{t("playground.chat.skill.freeformLabel")}</span>
              {!activeSkill && (
                <Check size={12} style={{ color: "var(--pg-blue)" }} />
              )}
            </button>
          </li>
          {skills.length > 0 && (
            <li
              className="my-1 border-t"
              style={{ borderColor: "var(--pg-border)" }}
              aria-hidden="true"
            />
          )}
          {skills.map((s) => {
            const selected =
              activeSkill !== null && activeSkill.id === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onSelect(s);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
                  style={{
                    color: selected
                      ? "var(--pg-blue-dk)"
                      : "var(--pg-text-2)",
                  }}
                >
                  <span className="flex flex-col">
                    <span>{s.name}</span>
                    <span
                      className="mono text-xs"
                      style={{ color: "var(--pg-text-3)" }}
                    >
                      v{s.current_version ?? "?"}
                    </span>
                  </span>
                  {selected && (
                    <Check size={12} style={{ color: "var(--pg-blue)" }} />
                  )}
                </button>
              </li>
            );
          })}
          <li
            className="my-1 border-t"
            style={{ borderColor: "var(--pg-border)" }}
            aria-hidden="true"
          />
          <li>
            <Link
              to={ROUTES.SKILLS}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
              style={{ color: "var(--pg-text-2)" }}
            >
              <Settings size={11} />
              {t("playground.chat.skill.manage")}
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
}


function formatIdr(n: number): string {
  return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(n);
}

function ModelChip({
  model,
  availableModels,
  catalogModels,
  disabled,
  onSelect,
}: {
  /** ``null`` = no model picked yet — the chip renders the placeholder. */
  model: string | null;
  availableModels: string[];
  catalogModels: CatalogModelEntry[];
  disabled: boolean;
  onSelect(next: string): void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!wrapRef.current || !target) return;
      if (!wrapRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const priceByModel = useMemo(() => {
    const map = new Map<string, CatalogModelEntry>();
    for (const entry of catalogModels) map.set(entry.model, entry);
    return map;
  }, [catalogModels]);

  // Search + pagination — a tenant's BYOK /v1/models list can be dozens
  // of entries, so filter by substring and page the results to keep the
  // dropdown compact.
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setQuery("");
      setPage(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableModels;
    return availableModels.filter((m) => m.toLowerCase().includes(q));
  }, [availableModels, query]);

  const PAGE_SIZE = 8;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const hasOptions = availableModels.length > 0;
  const modelInCatalog = model !== null && availableModels.includes(model);
  // Placeholder state: no model picked yet. The dot turns warn-tone
  // (same as "model not in catalog") so the user notices the chip
  // needs attention, and the label switches to the localised "pick a
  // model" copy.
  const placeholderActive = model === null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => {
          if (disabled || !hasOptions) return;
          setOpen((v) => !v);
        }}
        disabled={disabled || !hasOptions}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="mono inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          background: "var(--pg-bg)",
          border: "1px solid var(--pg-border)",
          color: "var(--pg-text-2)",
        }}
        title={
          hasOptions
            ? t("playground.chat.meta.modelSwitchTitle")
            : t("playground.chat.meta.modelNoProvidersTitle")
        }
      >
        <span
          aria-hidden="true"
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{
            background: modelInCatalog ? "var(--pg-blue)" : "var(--pg-warn)",
          }}
        />
        {placeholderActive
          ? t("playground.chat.meta.modelPickPlaceholder")
          : model}
        <ChevronDown size={11} style={{ color: "var(--pg-text-3)" }} />
      </button>
      {open && hasOptions && (
        <div
          className="absolute right-0 z-20 mt-1 w-[300px] rounded-lg border py-1 shadow-md"
          style={{
            background: "var(--pg-surface)",
            borderColor: "var(--pg-border)",
          }}
        >
          {/* Search */}
          <div className="px-2 pb-1.5 pt-1">
            <div
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5"
              style={{
                background: "var(--pg-bg)",
                border: "1px solid var(--pg-border)",
              }}
            >
              <Search size={12} style={{ color: "var(--pg-text-3)" }} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                placeholder={t("playground.chat.meta.modelSearchPlaceholder")}
                className="mono w-full bg-transparent text-xs outline-none"
                style={{ color: "var(--pg-text)" }}
              />
            </div>
          </div>
          {/* Options (paged) */}
          {pageItems.length > 0 ? (
            <ul role="listbox" className="max-h-72 overflow-y-auto">
              {pageItems.map((m) => {
                const entry = priceByModel.get(m);
                const priced =
                  entry?.has_pricing &&
                  entry.input_per_1m_idr != null &&
                  entry.output_per_1m_idr != null;
                return (
                  <li key={m}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={m === model}
                      onClick={() => {
                        onSelect(m);
                        setOpen(false);
                      }}
                      className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-[var(--pg-blue-soft)]/60"
                      style={{
                        color:
                          m === model ? "var(--pg-blue-dk)" : "var(--pg-text-2)",
                      }}
                    >
                      <span className="mono flex items-center justify-between text-xs">
                        <span className="truncate">{m}</span>
                        {m === model && (
                          <Check size={12} style={{ color: "var(--pg-blue)" }} />
                        )}
                      </span>
                      {priced ? (
                        <span
                          className="text-[10.5px] tabular-nums"
                          style={{ color: "var(--pg-text-3)" }}
                        >
                          {t("playground.chat.meta.pricePerM", {
                            in: formatIdr(entry!.input_per_1m_idr!),
                            out: formatIdr(entry!.output_per_1m_idr!),
                          })}
                        </span>
                      ) : (
                        <span
                          className="inline-flex w-fit items-center text-[10px] italic"
                          style={{ color: "var(--pg-text-3)" }}
                          title={t("playground.chat.meta.noPriceHint")}
                        >
                          {t("playground.chat.meta.noPrice")}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div
              className="px-3 py-4 text-center text-xs"
              style={{ color: "var(--pg-text-3)" }}
            >
              {t("playground.chat.meta.modelNoResults")}
            </div>
          )}
          {/* Pagination */}
          {pageCount > 1 && (
            <div
              className="flex items-center justify-between border-t px-2 py-1.5"
              style={{ borderColor: "var(--pg-border)" }}
            >
              <button
                type="button"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label={t("playground.chat.meta.modelPrevPage")}
                className="inline-flex items-center rounded p-1 transition-colors hover:bg-[var(--pg-blue-soft)]/60 disabled:cursor-not-allowed disabled:opacity-40"
                style={{ color: "var(--pg-text-2)" }}
              >
                <ChevronLeft size={14} />
              </button>
              <span
                className="mono text-[10px] tabular-nums"
                style={{ color: "var(--pg-text-3)" }}
              >
                {t("playground.chat.meta.modelPageInfo", {
                  a: pageStart + 1,
                  b: Math.min(filtered.length, pageStart + PAGE_SIZE),
                  n: filtered.length,
                })}
              </span>
              <button
                type="button"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                aria-label={t("playground.chat.meta.modelNextPage")}
                className="inline-flex items-center rounded p-1 transition-colors hover:bg-[var(--pg-blue-soft)]/60 disabled:cursor-not-allowed disabled:opacity-40"
                style={{ color: "var(--pg-text-2)" }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
          {/* Estimate note */}
          <div
            className="border-t px-3 pb-1 pt-1.5 text-[10px]"
            style={{ borderColor: "var(--pg-border)", color: "var(--pg-text-3)" }}
          >
            {t("playground.chat.meta.priceEstimateNote")}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemPromptCard({
  session,
  skills,
  onUpdate,
}: {
  session: PlaygroundSession;
  skills: Skill[];
  onUpdate(patch: Partial<PlaygroundSession>): void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.systemPrompt);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDraft(session.systemPrompt);
  }, [session.systemPrompt]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!pickerWrapRef.current || !target) return;
      if (!pickerWrapRef.current.contains(target)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pickerOpen]);

  const applySkill = (skill: Skill | null) => {
    if (skill === null) {
      onUpdate({ skillRef: null, systemPromptLocked: false });
    } else {
      const version = skill.current_version ?? 1;
      onUpdate({
        skillRef: { skillId: skill.id, version },
        systemPrompt: skill.current_version_snapshot?.system_prompt ?? "",
        systemPromptLocked: true,
      });
    }
    setPickerOpen(false);
  };

  return (
    <details
      open
      className="rounded-xl border"
      style={{
        borderColor: "var(--pg-border)",
        background: "var(--pg-surface)",
      }}
    >
      <summary
        className="flex cursor-pointer items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-xs"
        style={{ color: "var(--pg-text-2)" }}
      >
        <span className="flex items-center gap-2">
          <span className="pill pill--blue">{t("playground.chat.system.eyebrow")}</span>
          <span className="font-medium">{t("playground.chat.system.label")}</span>
        </span>
        <span className="flex items-center gap-3">
          {session.systemPromptLocked && (
            <span
              className="mono inline-flex items-center gap-1 text-xs"
              style={{ color: "var(--pg-text-3)" }}
            >
              <Lock size={11} />
              {t("playground.chat.system.locked")}
            </span>
          )}
          {/* "Pick from skill" — discoverable directly on the system
             prompt card. Mirrors the SkillChip in the header for
             operators who land here first. ``onClick`` stops the
             ``<details>`` toggle that the surrounding ``<summary>``
             would otherwise fire. */}
          <span
            ref={pickerWrapRef}
            className="relative"
            onClick={(e) => e.preventDefault()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPickerOpen((v) => !v);
              }}
              aria-expanded={pickerOpen}
              aria-haspopup="listbox"
              className="mono inline-flex items-center gap-1 rounded px-1.5 py-[2px] text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
              style={{ color: "var(--pg-blue-dk)" }}
            >
              <Sparkles size={11} style={{ color: "var(--pg-blue)" }} />
              {session.skillRef
                ? t("playground.chat.system.skillChange")
                : t("playground.chat.system.skillPick")}
              <ChevronDown size={11} />
            </button>
            {pickerOpen && (
              <ul
                role="listbox"
                className="absolute right-0 z-20 mt-1 max-h-72 w-[260px] overflow-y-auto rounded-lg border py-1 shadow-md"
                style={{
                  background: "var(--pg-surface)",
                  borderColor: "var(--pg-border)",
                }}
              >
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={session.skillRef === null}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      applySkill(null);
                    }}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
                    style={{ color: "var(--pg-text-2)" }}
                  >
                    <span>{t("playground.chat.skill.freeformLabel")}</span>
                    {session.skillRef === null && (
                      <Check size={12} style={{ color: "var(--pg-blue)" }} />
                    )}
                  </button>
                </li>
                {skills.length === 0 ? (
                  <li
                    className="px-3 py-2 text-xs"
                    style={{ color: "var(--pg-text-3)" }}
                  >
                    {t("playground.chat.system.skillNone")}
                  </li>
                ) : (
                  <>
                    <li
                      className="my-1 border-t"
                      style={{ borderColor: "var(--pg-border)" }}
                      aria-hidden="true"
                    />
                    {skills.map((s) => {
                      const selected =
                        session.skillRef !== null &&
                        session.skillRef.skillId === s.id;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              applySkill(s);
                            }}
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-[var(--pg-blue-soft)]/60"
                            style={{
                              color: selected
                                ? "var(--pg-blue-dk)"
                                : "var(--pg-text-2)",
                            }}
                          >
                            <span className="flex flex-col">
                              <span>{s.name}</span>
                              <span
                                className="mono text-xs"
                                style={{ color: "var(--pg-text-3)" }}
                              >
                                v{s.current_version ?? "?"}
                              </span>
                            </span>
                            {selected && (
                              <Check size={12} style={{ color: "var(--pg-blue)" }} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </>
                )}
              </ul>
            )}
          </span>
        </span>
      </summary>
      <div
        className="border-t px-4 py-3"
        style={{ borderColor: "var(--pg-border-soft)" }}
      >
        {editing && !session.systemPromptLocked ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              if (draft !== session.systemPrompt)
                onUpdate({ systemPrompt: draft });
              setEditing(false);
            }}
            autoFocus
            rows={4}
            className="mono block w-full resize-y rounded-md border bg-transparent p-2 text-xs outline-none"
            style={{ borderColor: "var(--pg-border)", color: "var(--pg-text)" }}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              if (!session.systemPromptLocked) setEditing(true);
            }}
            className="mono block w-full text-left text-xs leading-relaxed"
            style={{
              color: session.systemPrompt
                ? "var(--pg-text-2)"
                : "var(--pg-text-3)",
              cursor: session.systemPromptLocked ? "default" : "text",
            }}
          >
            {session.systemPrompt || t("playground.chat.system.empty")}
          </button>
        )}
      </div>
    </details>
  );
}

function EmptyPlaceholder() {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-xl border px-5 py-6 text-center text-xs"
      style={{
        borderColor: "var(--pg-border)",
        background: "var(--pg-surface)",
        color: "var(--pg-text-2)",
      }}
    >
      <p>{t("playground.chat.empty.title")}</p>
      <p
        className="mt-1 text-xs"
        style={{ color: "var(--pg-text-3)" }}
      >
        {t("playground.chat.empty.body")}
      </p>
    </div>
  );
}

function MessageRow({
  message,
  isBound,
  onBind,
}: {
  message: PlaygroundMessage;
  isBound: boolean;
  onBind(): void;
}) {
  if (message.role === "user") return <UserBubble message={message} />;
  return <AssistantBubble message={message} isBound={isBound} onBind={onBind} />;
}

function UserBubble({ message }: { message: PlaygroundMessage }) {
  const { t } = useTranslation();
  const ts = new Date(message.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return (
    <div className="flex flex-col items-end">
      <div
        className="mono mb-1 flex items-center gap-2 text-xs"
        style={{ color: "var(--pg-text-3)" }}
      >
        {message.redactedTypes && message.redactedTypes.length > 0 && (
          <span
            className="pill pill--warn"
            title={`Types: ${message.redactedTypes.join(", ")}`}
          >
            <ShieldAlert size={11} />
            {t("playground.chat.user.piiRedacted", {
              n: message.redactedTypes.length,
            })}
          </span>
        )}
        <span>{t("playground.chat.user.label")} · {ts}</span>
      </div>
      <div className="bubble bubble--user">{message.content}</div>
    </div>
  );
}

function AssistantBubble({
  message,
  isBound,
  onBind,
}: {
  message: PlaygroundMessage;
  isBound: boolean;
  onBind(): void;
}) {
  const { t } = useTranslation();
  const ts = new Date(message.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const isError = !!message.error;
  const isBlocked = message.error?.type?.includes("policy") || message.flagged === "policy";

  return (
    <div className="flex items-start gap-3">
      <div
        aria-hidden="true"
        className="mt-1 flex shrink-0 items-center justify-center rounded-lg border"
        style={{
          width: 30,
          height: 30,
          background: "var(--pg-blue-soft)",
          color: "var(--pg-blue)",
          borderColor: "var(--pg-blue-ring)",
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12 L8 8 L12 12 L16 8 L20 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="mono mb-1 flex items-center gap-2 text-xs"
          style={{ color: "var(--pg-text-3)" }}
        >
          <span>{t("playground.chat.assistant.brand")}</span>
          {message.model && <span>· {message.model}</span>}
          {message.timing && <span>· {message.timing.totalMs}ms</span>}
          {message.usage?.chunks !== undefined && (
            <span>· {message.usage.chunks} chunks</span>
          )}
          {message.usage?.tokens !== undefined && (
            <span>· {message.usage.tokens} tok</span>
          )}
          {message.flagged === "policy" && !isError && (
            <span className="pill pill--warn">
              {t("playground.chat.assistant.policyFlag")}
            </span>
          )}
          {message.content && !isError && (
            <CopyButton
              text={message.content}
              label={t("playground.chat.assistant.copy")}
              copiedLabel={t("playground.chat.assistant.copied")}
            />
          )}
        </div>

        <button
          type="button"
          onClick={onBind}
          className={cn(
            "block w-full cursor-pointer rounded-none p-0 text-left transition-colors",
            isBound && "ring-2 ring-offset-2 ring-offset-[var(--pg-bg)]"
          )}
          style={
            isBound
              ? { boxShadow: "0 0 0 2px var(--pg-blue-ring)" }
              : undefined
          }
        >
          {isError && isBlocked ? (
            <div className="bubble bubble--refusal">
              <div className="flex items-center gap-2 text-xs font-medium">
                <ShieldAlert size={14} />
                {t("playground.chat.assistant.policyBlocked")}
              </div>
              <div
                className="mono mt-1 text-xs"
                style={{ color: "var(--pg-danger)" }}
              >
                {message.error?.code} · {message.error?.message}
              </div>
            </div>
          ) : isError ? (
            <div className="bubble bubble--assistant" style={{ borderColor: "var(--pg-danger-ring)" }}>
              <div className="mono text-xs" style={{ color: "var(--pg-danger)" }}>
                {message.error?.code}: {message.error?.message}
              </div>
            </div>
          ) : (
            <div
              className={cn(
                "bubble bubble--assistant",
                message.content === "" && "stream-caret"
              )}
              aria-live="polite"
            >
              {message.content ? (
                <Markdown text={message.content} />
              ) : (
                <span style={{ color: "var(--pg-text-3)" }}>
                  {t("playground.chat.assistant.thinking")}
                </span>
              )}
            </div>
          )}
        </button>

        <div
          className="mono mt-1.5 flex items-center gap-2 text-xs"
          style={{ color: "var(--pg-text-3)" }}
        >
          <span>{ts}</span>
          {message.governance?.auditId && (
            <Link
              to={`${ROUTES.OBSERVABILITY}?audit=${encodeURIComponent(message.governance.auditId)}`}
              className="hover:underline"
              style={{ color: "var(--pg-blue-dk)" }}
            >
              audit:{message.governance.auditId.slice(0, 12)}…
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Copy-to-clipboard button for the assistant message. Sits in the
 * message meta row (outside the bind button so buttons do not nest).
 * Tooltip via ``title``; swaps to a check + "Copied" briefly on copy.
 */
function CopyButton({
  text,
  label,
  copiedLabel,
}: {
  text: string;
  label: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={copied ? copiedLabel : label}
      aria-label={copied ? copiedLabel : label}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="ml-auto inline-flex items-center gap-1 rounded px-1 py-[1px] transition-colors hover:text-[var(--pg-text-2)]"
      style={{ color: "var(--pg-text-3)" }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  );
}

